'use client';

/**
 * Availability-aware Morfi-handle suggestion.
 *
 * One flow for every surface that seeds a handle from an X username —
 * onboarding profile prefill and the connect-provider dialog. The rules:
 *
 *  - Prefer the normalized base (the X username) when it is valid AND the
 *    backend reports it available.
 *  - When taken, fall back to a `base_NNN` variant. The variant is minted
 *    ONCE per (base, attempt) inside the state transition, so it stays
 *    byte-stable across re-renders while its availability request is in
 *    flight — render-time regeneration would churn the query key every
 *    render.
 *  - A taken variant advances the attempt (bounded); `bump()` lets the
 *    caller request a fresh checked variant after a save-time uniqueness
 *    race.
 *  - Never returns a candidate the backend has called taken.
 */
import { c as _c } from "react/compiler-runtime";
import { useGetIdentityCheck } from '@morfi/backend-sdk-v2/client';
import { useState } from 'react';
const HANDLE_REGEX = /^[a-z0-9_]+$/;
const HANDLE_MIN = 3;
const HANDLE_MAX = 20;
/** Primary probe + this many `_NNN` variants before giving up. */
const MAX_VARIANT_ATTEMPTS = 3;
export type SuggestedHandleStatus = 'idle' | 'checking' | 'ready' | 'exhausted';
export interface SuggestedHandle {
  status: SuggestedHandleStatus;
  /** Verified-available candidate — null unless `status === 'ready'`. */
  suggestion: string | null;
  /** True once the backend has called the normalized base taken. */
  primaryTaken: boolean;
  /** Advance to the next checked variant (e.g. after a save-time race). */
  bump: () => void;
}

/**
 * Normalize a raw provider username into Morfi-handle shape, or null when
 * no valid candidate can be derived. Mirrors HandleInput's local rules.
 */
export function normalizeHandleCandidate(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string' || raw.length === 0) return null;
  const normalized = raw.toLowerCase().replace(/[^a-z0-9_]/g, '');
  if (normalized.length < HANDLE_MIN || normalized.length > HANDLE_MAX) return null;
  if (!HANDLE_REGEX.test(normalized)) return null;
  if (normalized.startsWith('_') || normalized.endsWith('_') || normalized.includes('__')) {
    return null;
  }
  return normalized;
}
function mintVariant(base: string): string {
  const randomBytes = new Uint16Array(1);
  crypto.getRandomValues(randomBytes);
  const suffix = ((randomBytes[0] ?? 0) % 900 + 100).toString();
  const maxBase = HANDLE_MAX - suffix.length - 1; // -1 for the underscore
  return `${base.slice(0, maxBase)}_${suffix}`;
}
interface AttemptState {
  base: string | null;
  attempt: number;
  /** The minted `base_NNN` candidate for attempts ≥ 1 — byte-stable per attempt. */
  variant: string | null;
}

/**
 * Advance to the next checked variant. Variants are minted exactly once per
 * (base, attempt) — inside this state transition, never during render — so
 * the availability query key stays byte-stable across re-renders while its
 * request is in flight.
 */
function advanceAttempt(current: AttemptState): AttemptState {
  if (current.attempt > MAX_VARIANT_ATTEMPTS) return current;
  return {
    base: current.base,
    attempt: current.attempt + 1,
    variant: current.base === null ? null : mintVariant(current.base)
  };
}
export function useSuggestedHandle(rawBase) {
  const $ = _c(14);
  let t0;
  if ($[0] !== rawBase) {
    t0 = normalizeHandleCandidate(rawBase);
    $[0] = rawBase;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const base = t0;
  let t1;
  if ($[2] !== base) {
    t1 = {
      base,
      attempt: 0,
      variant: null
    };
    $[2] = base;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [state, setState] = useState(t1);
  if (state.base !== base) {
    setState({
      base,
      attempt: 0,
      variant: null
    });
  }
  const {
    attempt
  } = state;
  let candidate = null;
  if (base !== null && state.base === base) {
    if (attempt === 0) {
      candidate = base;
    } else {
      if (attempt <= MAX_VARIANT_ATTEMPTS) {
        candidate = state.variant;
      }
    }
  }
  const enabled = candidate !== null;
  const t2 = candidate ?? "";
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["kind", "available"];
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2) {
    t4 = {
      kind: "handle",
      handle: t2,
      fields: t3
    };
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== enabled) {
    t5 = {
      enabled
    };
    $[7] = enabled;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const {
    data,
    isPending
  } = useGetIdentityCheck(t4, t5);
  const checked = enabled && !isPending && data !== null && data !== undefined;
  const available = checked && data.kind === "handle" && data.available === true;
  const taken = checked && data.kind === "handle" && data.available === false;
  if (taken && attempt <= MAX_VARIANT_ATTEMPTS) {
    setState(advanceAttempt);
  }
  const primaryTaken = attempt > 0;
  let status;
  if (base === null) {
    status = "idle";
  } else {
    if (attempt > MAX_VARIANT_ATTEMPTS) {
      status = "exhausted";
    } else {
      if (available) {
        status = "ready";
      } else {
        status = "checking";
      }
    }
  }
  const t6 = status === "ready" ? candidate : null;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setState(advanceAttempt);
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== primaryTaken || $[11] !== status || $[12] !== t6) {
    t8 = {
      status,
      suggestion: t6,
      primaryTaken,
      bump: t7
    };
    $[10] = primaryTaken;
    $[11] = status;
    $[12] = t6;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
}